/**
 * Liens web dans les messages (décision du porteur, vague 4). Le corps d'un message reste du texte
 * brut stocké tel quel ; à l'affichage, seules les URL http(s) deviennent cliquables, avec le nom de
 * domaine affiché en clair (anti-hameçonnage). Aucun aperçu, aucun appel serveur vers l'URL.
 * Fonction pure, utilisée par l'interface (garage et portail) et par l'API (limite de liens).
 */

/** Liens au plus par message (au-delà : refus 422 par l'API). */
export const MESSAGE_MAX_LINKS = 10;
/** Au-delà de cette longueur, une URL reste du texte (jamais cliquable). */
export const MAX_LINK_LENGTH = 2048;

export type TextSegment =
  | { type: 'text'; text: string }
  /** `href` : URL normalisée (http ou https) ; `domain` : nom d'hôte à afficher en clair à côté du lien. */
  | { type: 'link'; text: string; href: string; domain: string };

/** Attributs à poser sur chaque lien rendu. */
export const LINK_REL = 'noopener noreferrer nofollow ugc';
export const LINK_TARGET = '_blank';

/**
 * Caractères de contrôle de direction (audit S22, vague 6) : plongements et forçages U+202A à U+202E,
 * isolats U+2066 à U+2069, et marques U+200E, U+200F, U+061C. Dans un lien, ils retournent le texte
 * affiché **et** le domaine affiché juste après (anti-hameçonnage affaibli). Retirés partout.
 */
export const BIDI_CONTROLS = /[؜‎‏‪-‮⁦-⁩]/gu;

/** Retire les contrôles de direction d'un texte (affichage des messages, recopie dans les e-mails). */
export function stripBidiControls(text: string): string {
  return text.replace(BIDI_CONTROLS, '');
}

/** Candidat : « http:// » ou « https:// » non précédé d'une lettre, d'un chiffre ou d'une barre. */
const CANDIDATE = /(?<![\p{L}\p{N}/])https?:\/\/[^\s<>"'`]+/giu;
const TRAILING = /[.,;:!?»…'"]$/u;

/** Retire la ponctuation finale qui appartient à la phrase (« https://x.fr/p. »), et une parenthèse fermante orpheline. */
function trimTrailing(candidate: string): string {
  let url = candidate;
  for (;;) {
    if (TRAILING.test(url)) {
      url = url.slice(0, -1);
      continue;
    }
    const last = url.at(-1);
    const pairs: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
    const open = last ? pairs[last] : undefined;
    if (last && open && url.split(last).length > url.split(open).length) {
      url = url.slice(0, -1);
      continue;
    }
    return url;
  }
}

function toLink(raw: string): TextSegment | null {
  if (raw.length > MAX_LINK_LENGTH) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.hostname) return null;
  return { type: 'link', text: raw, href: url.href, domain: url.hostname };
}

/**
 * Découpe un texte en segments texte et lien. Les segments texte voisins sont fusionnés.
 * Vague 6 (S22) : les contrôles de direction sont retirés **avant** le découpage ; aucun segment
 * (texte, lien, domaine) n'en contient.
 */
export function extractLinks(input: string): TextSegment[] {
  const text = stripBidiControls(input);
  const out: TextSegment[] = [];
  const pushText = (t: string) => {
    if (!t) return;
    const prev = out.at(-1);
    if (prev?.type === 'text') prev.text += t;
    else out.push({ type: 'text', text: t });
  };
  let cursor = 0;
  for (const match of text.matchAll(CANDIDATE)) {
    const start = match.index;
    const raw = trimTrailing(match[0]);
    const link = toLink(raw);
    if (!link) continue;
    pushText(text.slice(cursor, start));
    out.push(link);
    cursor = start + raw.length;
  }
  pushText(text.slice(cursor));
  return out;
}

/** Nombre de liens cliquables d'un texte (contrôle de `MESSAGE_MAX_LINKS`). */
export function countLinks(text: string): number {
  return extractLinks(text).filter((s) => s.type === 'link').length;
}
